'use client';
import { useRef, useState } from 'react';
import { useFormikContext } from 'formik';
import {
  ExpressCheckoutElement,
  useElements,
  useStripe
} from '@stripe/react-stripe-js';

import './ApplicationWalletButtons.style.scss';

import Text from '@/components/common/Text/Text';
import { SignupState } from '@/types/Signup';

import { ApplicationFormValues } from './applicationValidation';
import { applyBillingMirror } from './addressSync';
import { confirmSetupIntent } from './confirmSetupIntent';
import {
  PaymentChoice,
  expressCheckoutMethods
} from './ApplicationPaymentSection';

type ApplicationWalletButtonsProps = {
  user: SignupState;
  /** The wallet the applicant picked; only its button renders. */
  choice: PaymentChoice;
  /**
   * Runs before the wallet sheet opens. Return false to keep it closed — the
   * caller is expected to have surfaced why (an error summary, inline errors).
   * Consent belongs here: it has to be given before the payment, not after.
   */
  onBeforeOpen: () => boolean;
  /** The application's own submit, once the payment method is confirmed. */
  onConfirmed: (setupIntentId: string) => Promise<void>;
  /** Reports the wallet the applicant actually used, for the recap. */
  onWalletTypeChange: (type: string | null) => void;
  /**
   * Send the billing address from the form instead of the wallet's, after the
   * wallet's was refused.
   */
  useFormBilling: boolean;
  /**
   * The chosen wallet's button could not render after all (Stripe reported it
   * unavailable, or the element failed to load), so the applicant needs to
   * pick another option.
   */
  onUnavailable: () => void;
};

/** Stripe's availability keys, by our choice value. */
const AVAILABILITY_KEY = {
  apple_pay: 'applePay',
  google_pay: 'googlePay',
  link: 'link'
} as const;

/**
 * Apple Pay and Google Pay, as their own buttons.
 *
 * Apple requires the payment sheet to open inside a user gesture on Apple's own
 * button, and Stripe owns that button inside its iframe — so a house-styled
 * "Submit and pay with Apple Pay" control cannot open it. The wallet button is
 * therefore the submit: one tap authorizes the payment method and provisions the
 * application.
 *
 * Renders just the button for the wallet the applicant picked, which
 * `WalletAvailabilityProbe` (in `ApplicationPaymentSection`) has already
 * confirmed the browser supports. Apple Pay uses `'always'` to match
 * the probe, so non-Safari browsers get Apple's scan-with-iPhone (QR code) flow.
 */
function ApplicationWalletButtons(props: ApplicationWalletButtonsProps) {
  const {
    user,
    choice,
    onUnavailable,
    onBeforeOpen,
    onConfirmed,
    onWalletTypeChange,
    useFormBilling
  } = props;

  const stripe = useStripe();
  const elements = useElements();
  const { values } = useFormikContext<ApplicationFormValues>();

  const [error, setError] = useState<string | null>(null);

  const applicantRef = useRef<SignupState>(user);
  applicantRef.current = { ...user, ...applyBillingMirror(values) };

  const handleConfirm = async (event: { expressPaymentType?: string }) => {
    if (!stripe || !elements) {
      return;
    }

    setError(null);
    // Which wallet actually paid, so the recap can name it.
    onWalletTypeChange(event.expressPaymentType ?? null);

    try {
      const setupIntentId = await confirmSetupIntent({
        stripe,
        elements,
        applicant: applicantRef.current,
        isWallet: true,
        useApplicantBilling: useFormBilling
      });

      await onConfirmed(setupIntentId);
    } catch (caught: any) {
      setError(caught?.message || 'We could not save your payment method.');
    }
  };

  return (
    <div
      className='ApplicationWalletButtons'
      data-testid='wallet-buttons'
    >
      <ExpressCheckoutElement
        options={{
          ...expressCheckoutMethods(choice),
          buttonType: { applePay: 'subscribe', googlePay: 'subscribe' },
          layout: { maxColumns: 1, maxRows: 1 }
        }}
        onClick={(event) => {
          // The gate: no sheet opens unless the application is actually ready to
          // submit, consents included. Not resolving leaves it closed.
          if (!onBeforeOpen()) {
            return;
          }

          event.resolve({ billingAddressRequired: true });
        }}
        onReady={({ availablePaymentMethods }) => {
          const key = AVAILABILITY_KEY[choice as keyof typeof AVAILABILITY_KEY];
          // `link` is missing from our 3.0.3 types; Stripe.js reports it.
          const available = availablePaymentMethods as
            | Record<string, boolean>
            | undefined;

          if (!key || !available?.[key]) {
            onUnavailable();
          }
        }}
        onLoadError={onUnavailable}
        onConfirm={handleConfirm}
      />

      {error && (
        <Text
          variant='text-s'
          className='ApplicationWalletButtons-error'
        >
          {error}
        </Text>
      )}
    </div>
  );
}

export default ApplicationWalletButtons;
